import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Tag,
  Input,
  Space,
  Avatar,
  Row,
  Col,
  Card,
  Statistic,
  message
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  FlagOutlined,
  CloseCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import Box from '@iso/components/utility/box';
import HazardPageWrapper from './Hazard.styles';
import { hazardService } from '@iso/services/hazard.service';
import {
  hazardStatus,
  hazardEscalationLevels,
  getStatusColor,
  getEscalationLevelShortLabel,
  HAZARD_CLOSED_STATUSES
} from '@iso/constants/hazardStatus';
import { getSafetyActivityHazardLabel } from '@iso/constants/hazardTypes';
import DeadlineCountdown from '@iso/components/Hazards/DeadlineCountdown';
import ModalActivity from '@iso/containers/Pages/HealthSafety/ModalActivity';
import { commonHelper } from '@iso/lib/helpers/commonHelper';
import { readBlobErrorMessage } from '@iso/lib/helpers/fileDownload';

import useHazardActions from './shared/useHazardActions';
import HazardActionModals from './shared/HazardActionModals';
import { getHazardActionColumn } from './shared/hazardActionColumns';

const HazardList = () => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const currentUserId = useSelector((state) => state.user?.user?.id);
  const activePermission = useSelector((state) => state.permission?.activePermission);

  // Export is admin-only. Hiding the button is presentation only — the API
  // rejects a non-admin caller regardless.
  const canExport = ['owner', 'admin'].includes(activePermission);

  const [hazards, setHazards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);

  // Search filter only
  const [searchText, setSearchText] = useState('');

  // Dynamic scroll based on viewport
  const [scrollX, setScrollX] = useState(window.innerWidth <= 991 ? 1200 : undefined);

  useEffect(() => {
    fetchHazards();
  }, [activePropertyId]);

  // Handle window resize for dynamic scroll
  useEffect(() => {
    const handleResize = () => {
      setScrollX(window.innerWidth <= 991 ? 800 : 1200);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for healthSafety activity refresh events
  useEffect(() => {
    const handleActivityRefresh = () => {
      fetchHazards();
    };

    window.addEventListener('healthSafety:activityRefresh', handleActivityRefresh);

    return () => {
      window.removeEventListener('healthSafety:activityRefresh', handleActivityRefresh);
    };
  }, [activePropertyId]);

  const fetchHazards = async () => {
    if (!activePropertyId) return;
    setLoading(true);

    try {
      const result = await hazardService.getList(activePropertyId);
      if (result && result.hazards) {
        setHazards(result.hazards);
      }
    } catch (error) {
      console.error('Failed to fetch hazards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!activePropertyId) return;
    setExporting(true);

    try {
      // Send the search box along so the file matches the rows on screen; the
      // other filters on this page are applied client-side only.
      const filters = searchText.trim() ? { search: searchText.trim() } : {};
      await hazardService.exportHazards(activePropertyId, filters);
      message.success('Hazards exported');
    } catch (error) {
      const apiMessage = await readBlobErrorMessage(error);
      message.error(apiMessage || 'Failed to export hazards');
    } finally {
      setExporting(false);
    }
  };

  const actions = useHazardActions({ onRefresh: fetchHazards });

  // Calculate statistics from hazards
  const stats = {
    totalOpen: hazards.filter(h => h.status === 'open' || h.status === 'in_progress').length,
    myAssigned: hazards.filter(h => h.workflow_state?.current_assignee_id === currentUserId).length,
    overdue: hazards.filter(h => h.workflow_state?.is_overdue && !HAZARD_CLOSED_STATUSES.includes(h.status)).length,
    level3: hazards.filter(h => h.current_escalation_level === 3 && (h.status === 'open' || h.status === 'in_progress')).length
  };

  // Check if current user can edit a hazard
  const canEditHazard = (hazard) => {
    if (!hazard || !currentUserId) return false;

    // Creator can edit
    if (hazard.creator_id === currentUserId) return true;

    // Current assignee can edit
    if (hazard.workflow_state?.current_assignee_id === currentUserId) return true;

    // Property role users can edit (this will be validated on backend)
    // For now, show edit option and let backend enforce permission
    return true;
  };

  // Simple search filter only - table column filters handle the rest
  const searchFilteredHazards = hazards.filter(hazard => {
    if (searchText) {
      const displayLabel = getSafetyActivityHazardLabel(hazard.hazard_type) || hazard.title;
      if (!displayLabel.toLowerCase().includes(searchText.toLowerCase())) {
        return false;
      }
    }
    return true;
  });

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => {
        // Map hazard_type to its label from hazardTypes.js
        const displayLabel = getSafetyActivityHazardLabel(record.hazard_type) || text;
        return (
          <Link to={`/hazards/${record.id}`}>
            <strong>{displayLabel}</strong>
          </Link>
        );
      },
      width: 250
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.replace(/_/g, ' ').toUpperCase()}
        </Tag>
      ),
      filters: hazardStatus.map(s => ({ text: s.label, value: s.value })),
      onFilter: (value, record) => record.status === value,
      filterMultiple: true,
      width: 100
    },
    {
      title: 'Level',
      dataIndex: 'current_escalation_level',
      key: 'level',
      render: (level) => (
        <Tag color={level === 3 ? 'red' : level === 2 ? 'orange' : 'blue'}>
          {getEscalationLevelShortLabel(level)}
        </Tag>
      ),
      filters: hazardEscalationLevels.map(l => ({ text: l.label, value: l.value })),
      onFilter: (value, record) => record.current_escalation_level === value,
      filterMultiple: true,
      width: 100
    },
    {
      title: 'Assigned To',
      key: 'assigned',
      render: (_, record) => {
        const assignee = record.workflow_state?.current_assignee;
        if (!assignee) return '-';
        return (
          <Space size="small">
            <Avatar size="small" icon={<UserOutlined />} />
            <span>{assignee.name}</span>
          </Space>
        );
      },
      filters: [
        { text: 'Assigned to Me', value: 'me' },
        { text: 'Others', value: 'others' },
      ],
      onFilter: (value, record) => {
        const assigneeId = record.workflow_state?.current_assignee_id;
        if (value === 'me') {
          return assigneeId === currentUserId;
        } else {
          return assigneeId !== currentUserId;
        }
      },
      width: 180
    },
    {
      title: 'Deadline',
      dataIndex: 'deadline',
      key: 'deadline',
      render: (deadline, record) => {
        if (!deadline) return '-';
        const isClosed = HAZARD_CLOSED_STATUSES.includes(record.status);
        return (
          <Space direction="vertical" size="small">
            <div style={{ fontSize: 12 }}>{commonHelper.displayDateTimeLocal(deadline, 'DD/MM/YY HH:mm')}</div>
            {!isClosed && <DeadlineCountdown deadline={deadline} compact />}
          </Space>
        );
      },
      width: 150
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => commonHelper.displayDateLocal(date),
      width: 100
    },
    getHazardActionColumn({
      canEdit: true,
      showViewLink: true,
      canEditHazard,
      handlers: actions,
    }),
  ];

  return (
    <LayoutWrapper>
      <HazardPageWrapper>
        <Box id="containerBox">
          <div className="manage-property-header">
            <h2 className="manage-property-title" style={{ margin: 0 }}>Hazards</h2>
          </div>

          <Card
            className="holiday-card"
            title={
              <div className="card-header">
                <span className="card-title">Hazard Management</span>
                <div className="card-actions">
                  <Input
                    placeholder="Search…"
                    className="hazard-search-input"
                    suffix={
                      !searchText && (
                        <SearchOutlined className="site-form-item-icon" />
                      )
                    }
                    prefix={
                      searchText && (
                        <CloseCircleOutlined
                          onClick={() => setSearchText('')}
                          className="site-form-item-cancel"
                        />
                      )
                    }
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 250, height: 40, marginRight: 8 }}
                  />
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchHazards}
                    loading={loading}
                    className="hazard-refresh-button"
                    style={{ height: 40, borderRadius: 8, marginRight: 8 }}
                  >
                    Refresh
                  </Button>
                  {canExport && (
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={handleExport}
                      loading={exporting}
                      disabled={!activePropertyId}
                      className="hazard-export-button"
                      style={{ height: 40, borderRadius: 8, marginRight: 8 }}
                    >
                      Export
                    </Button>
                  )}
                  <Button
                    type="primary"
                    className="btn-success"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                  >
                    Create Hazard
                  </Button>
                </div>
              </div>
            }
          >
            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Total Open Hazards"
                    value={stats.totalOpen}
                    prefix={<WarningOutlined />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Card className="stat-card">
                  <Statistic
                    title="My Assigned Hazards"
                    value={stats.myAssigned}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: 'rgba(0, 0, 0, 0.85)' }}
                  />
                </Card>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Overdue Hazards"
                    value={stats.overdue}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#f5222d' }}
                  />
                </Card>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Card
                  className="stat-card"
                  style={{ borderColor: '#f5222d', borderWidth: 2 }}
                >
                  <Statistic
                    title="Level 3 (URGENT)"
                    value={stats.level3}
                    prefix={<FlagOutlined />}
                    valueStyle={{ color: '#f5222d', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
            </Row>

            <div style={{ marginBottom: 16, fontSize: 13, color: '#666' }}>
              Showing {searchFilteredHazards.length} of {hazards.length} hazards
            </div>

            {/* Table */}
            <Table
              columns={columns}
              dataSource={searchFilteredHazards}
              loading={loading}
              rowKey="id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} hazards`
              }}
              scroll={{ x: scrollX }}
              className="hazard-table"
            />
          </Card>

          {/* Create Modal */}
          <ModalActivity
            visibleModalActivity={createModalVisible}
            cancelModalActivity={() => setCreateModalVisible(false)}
            activityActive="hazard"
            editingPolicy={null}
            propertyId={activePropertyId}
            searchAssetAndPolicy={fetchHazards}
            saveToHazardsTable={true}
          />

          {/* Shared Action Modals (edit, resolve, escalate, reopen, confirm) */}
          <HazardActionModals
            {...actions}
            propertyId={activePropertyId}
            onRefresh={fetchHazards}
          />
        </Box>
      </HazardPageWrapper>
    </LayoutWrapper>
  );
};

export default HazardList;
